import { Action, AnyAction, applyMiddleware, combineReducers, compose, createStore, Reducer } from 'redux';
import reduxThunk, { ThunkDispatch } from 'redux-thunk';
import { isBlank } from 'utils/isBlank';
import { isPresent } from 'utils/isPresent';

import UnknownObject from '../../../types/UnknownObject';

import { receivedServerData } from './DataFetcher';
import reducerRegistry from './ReducerRegistry';

const combine = (reducers: Record<string, Reducer>, staticReducers: Record<string, Reducer>): Reducer => {
  if (isBlank(reducers) && isBlank(staticReducers)) {
    return (state: unknown) => state;
  }
  return combineReducers({
    ...staticReducers,
    ...reducers,
  });
};

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

function initialStateForStaticReducers(initialState: UnknownObject, staticReducerNames: string[]) {
  const existingReducerInitialStateKeys = Object.keys(initialState).filter((x) => staticReducerNames.includes(x));
  return existingReducerInitialStateKeys.reduce(
    (result, key) => ({
      ...result,
      [key]: initialState[key],
    }),
    {}
  );
}

function initialStateForDynamicReducers(initialState: UnknownObject, staticReducerNames: string[]) {
  const remainingKeys = Object.keys(initialState).filter((x) => staticReducerNames.includes(x) === false);
  return remainingKeys.reduce(
    (result, key) => ({
      ...result,
      [key]: initialState[key],
    }),
    {} as UnknownObject
  );
}

export function createReduxStoreWithInitialState(staticReducers: Record<string, Reducer>, initialState: UnknownObject) {
  const staticReducerInitialState = initialStateForStaticReducers(initialState, Object.keys(staticReducers));
  const dynamicReducerInitialState = initialStateForDynamicReducers(initialState, Object.keys(staticReducers));

  const store = createStore(
    combine(reducerRegistry.getReducers(), staticReducers),
    staticReducerInitialState,
    composeEnhancers(applyMiddleware(reduxThunk))
  );

  reducerRegistry.setChangeListener((reducers: Record<string, Reducer>) => {
    store.replaceReducer(combine(reducers, staticReducers));
  });

  Object.keys(dynamicReducerInitialState).forEach((key) => {
    const value = dynamicReducerInitialState[key as keyof typeof dynamicReducerInitialState];
    if (isPresent(value)) {
      const data = {
        [key]: value,
      };
      receivedServerData(data);
    }
  });

  return store;
}

export function createReduxStore(staticReducers: Record<string, Reducer>) {
  return createReduxStoreWithInitialState(staticReducers, {});
}

export type AppDispatch<State = unknown, ActionType extends Action = AnyAction> = ThunkDispatch<State, any, ActionType>;
